import { useState } from 'react'
import { leggiFile } from '@cidis/shared/rubrica-file'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Etichetta, Modale, Spunta, Tabella } from '../../componenti/base.tsx'
import { IconaCarica, IconaScarica } from '../../componenti/Icone.tsx'
import {
  anteprimaImportazione, esportaRubrica, importa, MASSIMO_VOCI, testoDelFile,
  type AnteprimaImportazione, type StatoImportazione,
} from '../../servizi/posta-messaggio.ts'
import '../../stile-posta-messaggio.css'

/**
 * Importazione ed esportazione della rubrica (539, come in Roundcube).
 *
 * Importa: vCard (.vcf, 3.0 e 4.0) e CSV di Gmail e di Outlook. Il file si legge qui, nel browser
 * (packages/shared/src/rubrica-file.ts), e all'API arrivano indirizzo, nome e gruppi. Prima l'anteprima, con i
 * doppioni e le voci che non vanno; poi «Importa». I contatti gia' in rubrica non cambiano, salvo prendere il
 * nome se non l'avevano.
 *
 * Esporta: vCard o CSV, con i gruppi. L'esportazione porta fuori dati personali e resta nel registro con il
 * numero dei contatti. La fa chi corregge la rubrica.
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const STATI: Record<StatoImportazione, { testo: string; tono: 'positivo' | 'neutro' | 'attenzione' | 'critico' }> = {
  nuovo: { testo: 'nuovo', tono: 'positivo' },
  presente: { testo: 'già in rubrica', tono: 'neutro' },
  ripetuto: { testo: 'doppione nel file', tono: 'attenzione' },
  casella: { testo: 'è la casella', tono: 'neutro' },
  non_valido: { testo: 'indirizzo non valido', tono: 'critico' },
}

export function ComandiFileRubrica({ casellaId, importato, esito }: {
  casellaId: string
  /** Dopo l'importazione: la pagina rilegge la rubrica e dice il risultato. */
  importato: (testo: string) => void
  esito: (errore: string | null) => void
}) {
  const [importa, setImporta] = useState(false)
  const [inCorso, setInCorso] = useState<'vcf' | 'csv' | null>(null)

  async function esporta(formato: 'vcf' | 'csv') {
    setInCorso(formato); esito(null)
    try { await esportaRubrica(casellaId, formato) } catch (e) { esito(testoErrore(e)) } finally { setInCorso(null) }
  }

  return (
    <div className="rubrica-comandi">
      <Bottone dimensione="piccolo" icona={<IconaCarica />} onClick={() => setImporta(true)}>Importa da file</Bottone>
      <Bottone dimensione="piccolo" variante="discreto" icona={<IconaScarica />} inCorso={inCorso === 'vcf'}
               disabled={!!inCorso} onClick={() => void esporta('vcf')}>Esporta in vCard</Bottone>
      <Bottone dimensione="piccolo" variante="discreto" icona={<IconaScarica />} inCorso={inCorso === 'csv'}
               disabled={!!inCorso} onClick={() => void esporta('csv')}>Esporta in CSV</Bottone>
      {importa && (
        <ImportaRubrica casellaId={casellaId} chiudi={() => setImporta(false)}
                        fatto={(testo) => { setImporta(false); importato(testo) }} />
      )}
    </div>
  )
}

function ImportaRubrica({ casellaId, chiudi, fatto }: {
  casellaId: string; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [nomeFile, setNomeFile] = useState<string | null>(null)
  const [anteprima, setAnteprima] = useState<AnteprimaImportazione | null>(null)
  const [conGruppi, setConGruppi] = useState(true)
  const [fase, setFase] = useState<'scelta' | 'lettura' | 'anteprima' | 'importazione'>('scelta')
  const [errore, setErrore] = useState<string | null>(null)

  async function scegli(file: File) {
    setErrore(null); setAnteprima(null); setNomeFile(file.name); setFase('lettura')
    try {
      const voci = leggiFile(await testoDelFile(file))
      if (!voci.length) throw new Error('Nel file non ci sono indirizzi email: serve una vCard (.vcf) o un CSV con una colonna di indirizzi.')
      if (voci.length > MASSIMO_VOCI) {
        throw new Error(`Il file ha ${voci.length} indirizzi: se ne importano al massimo ${MASSIMO_VOCI} per volta. Dividilo in più file.`)
      }
      setAnteprima(await anteprimaImportazione(casellaId, voci))
      setFase('anteprima')
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : (e as Error).message)
      setFase('scelta')
    }
  }

  async function conferma() {
    if (!anteprima) return
    setFase('importazione'); setErrore(null)
    try {
      const voci = anteprima.voci.filter((v) => v.stato === 'nuovo' || v.stato === 'presente')
        .map(({ indirizzo, nome, gruppi }) => ({ indirizzo, nome, gruppi }))
      const r = await importa(casellaId, voci, conGruppi)
      fatto(`${r.importati === 1 ? '1 contatto importato' : `${r.importati} contatti importati`}`
        + (r.gruppi_creati ? `, ${r.gruppi_creati === 1 ? '1 gruppo nuovo' : `${r.gruppi_creati} gruppi nuovi`}` : '') + '.')
    } catch (e) {
      setErrore(testoErrore(e))
      setFase('anteprima')
    }
  }

  const r = anteprima?.riepilogo
  const gruppiNelFile = [...new Set(anteprima?.voci.flatMap((v) => v.gruppi) ?? [])]
  return (
    <Modale larga titolo="Importa contatti" chiudi={chiudi} azioni={(
      <>
        <Bottone onClick={chiudi}>Annulla</Bottone>
        {anteprima && (
          <Bottone variante="primario" inCorso={fase === 'importazione'} disabled={!r?.nuovi && !r?.presenti}
                   onClick={() => void conferma()}>
            {r?.nuovi === 1 ? 'Importa 1 contatto' : `Importa ${r?.nuovi ?? 0} contatti`}
          </Bottone>
        )}
      </>
    )}>
      <p>
        Scegli un file vCard (.vcf) o CSV, come lo esportano Gmail, Outlook, Thunderbird o il telefono. Il file si
        legge su questo computer: al gestionale arrivano solo nomi, indirizzi e gruppi.
      </p>
      <div className="riga">
        <label className="bottone bottone-secondario bottone-piccolo scelta-file">
          <IconaCarica /> {nomeFile ? 'Scegli un altro file' : 'Scegli il file'}
          <input type="file" hidden accept=".vcf,.vcard,.csv,.txt,text/vcard,text/x-vcard,text/csv" onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void scegli(file)
            e.target.value = ''
          }} />
        </label>
        {nomeFile && <span className="piccolo tenue">{nomeFile}</span>}
      </div>
      {fase === 'lettura' && <Attesa testo="Lettura del file…" />}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {anteprima && r && (
        <>
          <div className="rubrica-anteprima-riepilogo" aria-live="polite">
            <Etichetta tono="positivo">{r.nuovi === 1 ? '1 nuovo' : `${r.nuovi} nuovi`}</Etichetta>
            {r.presenti > 0 && <Etichetta>{r.presenti} già in rubrica</Etichetta>}
            {r.ripetuti > 0 && <Etichetta tono="attenzione">{r.ripetuti === 1 ? '1 doppione' : `${r.ripetuti} doppioni`} nel file</Etichetta>}
            {r.non_validi > 0 && <Etichetta tono="critico">{r.non_validi === 1 ? '1 non valido' : `${r.non_validi} non validi`}</Etichetta>}
          </div>
          <p className="piccolo tenue">
            Entrano i nuovi. I contatti già in rubrica restano come sono; prendono il nome del file solo se non ne
            avevano uno. Doppioni e indirizzi non validi non si importano.
          </p>
          {gruppiNelFile.length > 0 && (
            <Spunta etichetta={`Metti i contatti nei gruppi del file (${gruppiNelFile.slice(0, 5).join(', ')}${gruppiNelFile.length > 5 ? '…' : ''})`}
                    valore={conGruppi} cambia={setConGruppi}
                    aiuto="I gruppi che mancano si creano. Senza, i contatti entrano e basta." />
          )}
          <div className="rubrica-anteprima-elenco">
            <Tabella intestazioni={['Indirizzo', 'Nome', 'Esito']}>
              {anteprima.voci.map((v, i) => (
                <tr key={i}>
                  <td data-etichetta="Indirizzo" className="mono">{v.indirizzo}</td>
                  <td data-etichetta="Nome">{v.nome ?? <span className="tenue">—</span>}</td>
                  <td data-etichetta="Esito"><Etichetta tono={STATI[v.stato].tono}>{STATI[v.stato].testo}</Etichetta></td>
                </tr>
              ))}
            </Tabella>
          </div>
        </>
      )}
    </Modale>
  )
}
